import React, { useState } from "react";
import cns from "classnames";
import {
  Collapse,
  IconButton,
  Typography,
  Paper,
  makeStyles,
  Box
} from "@material-ui/core";
import UpIcon from "react-icons/lib/md/keyboard-arrow-up";
import DownIcon from "react-icons/lib/md/keyboard-arrow-down";

import { ThemeType } from "@theme";

import { Subheading } from "./typography/Subheading";

type Props = {
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  children: React.ReactNode;
  /**
   * true by default
   */
  collapsible?: boolean;
  error?: boolean;
  fitted?: boolean;
  title?: React.ReactNode;
  innerTitle?: React.ReactNode;
  initiallyCollapsed?: boolean;
  style?: React.CSSProperties;
  collapsibleContainerStyle?: React.CSSProperties;
  flatCornersTop?: boolean;
};

/**
 * A section of information on profile and similar pages
 * The title is collapsible
 */
function ProfileSection(props: Props) {
  const [collapsed, setCollapsed] = useState(Boolean(props.initiallyCollapsed));
  const classes = useStyles({});
  const {
    contentClassName: contentClassNameProp,
    contentStyle,
    error,
    fitted,
    title,
    innerTitle,
    collapsible,
    children,
    collapsibleContainerStyle,
    flatCornersTop,
    ...r
  } = props;

  const collapseClassName = cns({
    [classes.collapseIn]: collapsed,
    [classes.collapseOut]: !collapsed
  });

  const contentClassName = cns(contentClassNameProp, classes.content, {
    [classes.contentPadded]: !fitted,
    [classes.contentError]: props.error
  });

  const flatTopCornersClassName = cns(contentClassNameProp, classes.flatCorners, {
    [classes.contentPadded]: !fitted,
    [classes.contentError]: props.error
  });

  return (
    <div {...r}>
      {(title || collapsible) && (
        <div className={classes.header}>
          {title && typeof title === "string" && (
            <div style={{ flex: "1 0 0" }}>
              <Subheading className={classes.title}>{title}</Subheading>
            </div>
          )}

          {title && typeof title !== "string" && title}

          {collapsible && (
            <div style={{ flex: "0 0 0" }}>
              <IconButton onClick={(e) => setCollapsed(!collapsed)}>
                {collapsed && <DownIcon />}
                {!collapsed && <UpIcon />}
              </IconButton>
            </div>
          )}
        </div>
      )}

      <Collapse
        in={!collapsed}
        className={collapseClassName}
        style={collapsibleContainerStyle}
      >
        <Typography
          component={Paper as any}
          className={flatCornersTop ? flatTopCornersClassName : contentClassName}
          style={contentStyle}
        >
          {innerTitle && (
            <Box p={2} pb={1}>
              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                {innerTitle}
              </Typography>
            </Box>
          )}

          {children}
        </Typography>
      </Collapse>
    </div>
  );
}

ProfileSection.defaultProps = {
  collapsible: true
};

const useStyles = makeStyles((theme: ThemeType) => ({
  header: {
    alignItems: "center",
    display: "flex"
    // minHeight: 48 // item button height
  },

  title: {
    textTransform: "uppercase"
  },

  content: {
    position: "relative"
  },

  flatCorners: {
    position: "relative",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0
  },

  contentError: {
    background: theme.palette.error.background
  },

  contentPadded: {
    padding: theme.spacing(2)
  },

  collapseOut: {
    overflow: "visible"
  },

  collapseIn: {
    overflow: "hidden"
  }
}));

export default ProfileSection;
